'use client';

import React from 'react';
import { Star, Quote } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { containerClass, sectionStyle } from './HeaderSections';

const DEFAULT_TESTIMONIALS = [
  {
    name: 'Sara Bennani',
    role: 'Family holiday · Casablanca',
    text: 'The car was spotless and the team delivered it straight to our riad. No queue, no paperwork — exactly what you want after a flight.',
    rating: 5,
    avatar: 'https://i.pravatar.cc/120?img=47',
  },
  {
    name: 'James Whitfield',
    role: 'Business trip · London',
    text: 'Booked at 11pm for an 8am pick-up. Confirmation in two minutes and the keys were ready at the airport desk.',
    rating: 5,
    avatar: 'https://i.pravatar.cc/120?img=12',
  },
  {
    name: 'Youssef El Amrani',
    role: 'Surf week · Taghazout',
    text: 'Roof racks fitted for free and unlimited kilometres meant we could chase every break down the coast without worrying.',
    rating: 5,
    avatar: 'https://i.pravatar.cc/120?img=33',
  },
];

function Stars({ n = 5 }: { n?: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`h-4 w-4 ${i < n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// 1. Grid of testimonial cards
// ──────────────────────────────────────────────────────────────

export function TestimonialsGrid({ content, style }: SectionProps) {
  const {
    eyebrow = 'Guest reviews',
    headline = 'Loved by travellers',
    subheadline = 'A few words from people who drove with us this season.',
    items = DEFAULT_TESTIMONIALS,
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{headline}</h2>
          {subheadline && <p className="mt-3 text-base text-slate-600">{subheadline}</p>}
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {items.map((t: any, i: number) => (
            <figure key={i} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6">
              <Stars n={t.rating ?? 5} />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">“{t.text}”</blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
                {t.avatar && <img src={t.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />}
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 2. Single featured quote
// ──────────────────────────────────────────────────────────────

export function TestimonialsFeatured({ content, style }: SectionProps) {
  const {
    quote = 'We have rented from every agency in Agadir over the years. This is the first one where the price we booked online was the price we paid at the counter.',
    name = 'Laila Mansouri',
    role = 'Returning guest · Marrakech',
    avatar = 'https://i.pravatar.cc/160?img=45',
    rating = 5,
    stats = [
      { value: '4.9/5', label: 'Average rating' },
      { value: '2,400+', label: 'Reviews' },
      { value: '98%', label: 'Would book again' },
    ],
  } = content;

  return (
    <section className="w-full bg-slate-900" style={sectionStyle(style)}>
      <div className={`${containerClass(style)} py-4`}>
        <div className="mx-auto max-w-3xl text-center">
          <Quote className="mx-auto h-8 w-8 text-brand" />
          <blockquote className="mt-6 text-xl font-medium leading-relaxed text-white sm:text-2xl">“{quote}”</blockquote>
          <div className="mt-7 flex items-center justify-center gap-3">
            {avatar && <img src={avatar} alt="" className="h-12 w-12 rounded-full object-cover" />}
            <div className="text-left">
              <p className="text-sm font-semibold text-white">{name}</p>
              <p className="text-xs text-white/60">{role}</p>
            </div>
          </div>
          <div className="mt-4 flex justify-center">
            <Stars n={rating} />
          </div>

          {stats.length > 0 && (
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/15 pt-6">
              {stats.map((s: any, i: number) => (
                <div key={i}>
                  <p className="text-2xl font-bold text-white">{s.value}</p>
                  <p className="text-xs uppercase tracking-wide text-white/60">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 3. Compact two-column review list
// ──────────────────────────────────────────────────────────────

export function TestimonialsCompact({ content, style }: SectionProps) {
  const {
    headline = 'What guests say',
    subheadline = 'Verified reviews from recent rentals.',
    items = DEFAULT_TESTIMONIALS,
  } = content;

  return (
    <section className="w-full bg-slate-50" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="grid gap-10 lg:grid-cols-[1fr_2fr]">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">{headline}</h2>
            {subheadline && <p className="mt-3 text-sm text-slate-600">{subheadline}</p>}
            <div className="mt-6 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-4xl font-bold text-slate-900">4.9</p>
              <div>
                <Stars />
                <p className="mt-1 text-xs text-slate-500">Based on 2,400+ reviews</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {items.map((t: any, i: number) => (
              <article key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {t.avatar && <img src={t.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />}
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.role}</p>
                    </div>
                  </div>
                  <Stars n={t.rating ?? 5} />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">{t.text}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export const TESTIMONIAL_VARIATIONS = {
  grid: TestimonialsGrid,
  featured: TestimonialsFeatured,
  compact: TestimonialsCompact,
} as const;

export type TestimonialVariationKey = keyof typeof TESTIMONIAL_VARIATIONS;
